import { useMemo, useState } from "react";
import { BookOpenCheck, Clock3, Search, UserRoundSearch } from "lucide-react";
import { useAuth } from "../auth/AuthProvider";
import { useOrganization } from "../organization/useOrganization";
import { useScheduling } from "../scheduling/useScheduling";
import { Card, EmptyState, StatCard } from "../../shared/ui/Card";
import { PageSkeleton, StatusBadge } from "../../shared/ui/Status";
import { useSales } from "./useSales";
import { courseUnitBreakdown, scopedCoursePackages } from "./memberCourseInquiry";

export function MemberCourseInquiryPanel() {
  const { session } = useAuth();
  const organization = useOrganization();
  const sales = useSales();
  const scheduling = useScheduling();
  const [query, setQuery] = useState("");
  const data = useMemo(() => {
    if (!session || !organization.data || !sales.data || !scheduling.data)
      return null;
    if (session.profile.role !== "employee" && session.profile.role !== "manager")
      return null;
    const packages = scopedCoursePackages(
      session.profile.role,
      session.profile.id,
      session.profile.storeId,
      organization.data.accounts,
      sales.data.packages,
    );
    const memberIds = new Set(packages.map((item) => item.memberId));
    const members = organization.data.accounts.filter(
      (item) => item.role === "member" && memberIds.has(item.id),
    );
    return { packages, members };
  }, [organization.data, sales.data, scheduling.data, session]);

  if (!session || !organization.data || !sales.data || !scheduling.data || !data)
    return <PageSkeleton />;

  const keyword = query.trim().toLocaleLowerCase("zh-CN");
  const accountNames = Object.fromEntries(
    organization.data.accounts.map((account) => [account.id, account.name]),
  );
  const storeNames = Object.fromEntries(
    organization.data.stores.map((store) => [store.id, store.name]),
  );
  const matchedMembers = keyword
    ? data.members.filter((member) => {
        const courses = data.packages.filter((item) => item.memberId === member.id);
        return [member.name, member.phone, ...courses.flatMap((item) => [item.productName, item.beneficiaryName])]
          .some((value) => value.toLocaleLowerCase("zh-CN").includes(keyword));
      })
    : [];
  const totalRemaining = data.packages.reduce(
    (sum, item) => sum + Math.max(0, item.totalUnits - item.usedUnits),
    0,
  );
  const totalUsed = data.packages.reduce((sum, item) => sum + item.usedUnits, 0);

  return (
    <section className="page-stack member-course-inquiry">
      <div className="section-divider">
        <span>课程资产查询</span>
        <h2>会员报课与销课</h2>
      </div>
      <div className="stat-grid">
        <StatCard label="可查询会员" value={`${data.members.length}`} detail={session.profile.role === "manager" ? "本店会员" : "本人主责或协作会员"} />
        <StatCard label="课程包" value={`${data.packages.length}`} detail="含在用、到期与已销完" />
        <StatCard label="累计已销" value={unit(totalUsed)} detail="历史期初与新系统合计" />
        <StatCard label="当前剩余" value={unit(totalRemaining)} detail="所有可见课程包合计" />
      </div>
      <Card className="course-inquiry-search">
        <UserRoundSearch />
        <label>
          <span>输入会员姓名、手机号或课程名称</span>
          <div>
            <Search size={17} />
            <input
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="例如：张三 / 13800000000 / 私教课"
              autoComplete="off"
            />
          </div>
        </label>
      </Card>
      {!keyword ? (
        <EmptyState title="请输入查询条件" description="员工查询本人主责或协作服务的会员；店长查询本店会员。" />
      ) : matchedMembers.length ? (
        <div className="course-inquiry-results">
          {matchedMembers.map((member) => {
            const packages = data.packages.filter((item) => item.memberId === member.id);
            const summary = packages.reduce(
              (result, item) => {
                const breakdown = courseUnitBreakdown(item, scheduling.data.consumptions);
                result.used += breakdown.used;
                result.remaining += breakdown.remaining;
                return result;
              },
              { used: 0, remaining: 0 },
            );
            return (
              <Card className="course-member-result" key={member.id}>
                <header>
                  <div>
                    <span>{storeNames[member.storeId ?? ""] ?? "跨店会员"}</span>
                    <h3>{member.name}</h3>
                    <p>{member.phone} · {packages.length} 个课程包</p>
                  </div>
                  <div className="course-member-totals">
                    <strong>{unit(summary.remaining)}<small>剩余</small></strong>
                    <strong>{unit(summary.used)}<small>已销</small></strong>
                  </div>
                </header>
                <div className="course-package-inquiry-list">
                  {packages.map((item) => {
                    const breakdown = courseUnitBreakdown(item, scheduling.data.consumptions);
                    const records = scheduling.data.consumptions
                      .filter((record) => record.packageId === item.id)
                      .sort((a, b) => b.completedAt.localeCompare(a.completedAt));
                    return (
                      <details key={item.id}>
                        <summary>
                          <BookOpenCheck />
                          <div>
                            <span>{item.beneficiaryName || member.name}</span>
                            <strong>{item.productName}</strong>
                            <small>负责教练 {accountNames[item.coachId] ?? "未找到"} · 有效期至 {date(item.validTo)}</small>
                          </div>
                          <StatusBadge tone={item.status === "active" ? "success" : item.status === "closed" ? "neutral" : "warning"}>{packageStatus(item.status)}</StatusBadge>
                        </summary>
                        <div className="course-unit-grid">
                          <div><span>购买</span><strong>{unit(breakdown.purchased)}</strong></div>
                          <div><span>赠送</span><strong>{unit(breakdown.gifted)}</strong></div>
                          <div><span>已销</span><strong>{unit(breakdown.used)}</strong></div>
                          <div><span>剩余</span><strong>{unit(breakdown.remaining)}</strong></div>
                        </div>
                        <div className="course-consumption-copy">
                          {breakdown.openingUsed > 0 && <p>历史系统期初已销 {unit(breakdown.openingUsed)} 课时，无逐笔销课记录。</p>}
                          <p>新系统有效销课 {unit(breakdown.activeSystemUsed)} 课时；已撤销记录不计入已销和剩余。</p>
                        </div>
                        {records.length ? (
                          <div className="course-consumption-list">
                            {records.map((record) => {
                              const schedule = scheduling.data.schedules.find((item) => item.id === record.scheduleId);
                              return (
                                <article key={record.id}>
                                  <Clock3 />
                                  <div>
                                    <strong>{dateTime(record.completedAt)} · {unit(record.units)}课时</strong>
                                    <small>授课教练 {schedule ? accountNames[schedule.coachId] ?? "未知" : "历史记录"} · 操作人 {accountNames[record.operatorId] ?? "未知"}</small>
                                  </div>
                                  <StatusBadge tone={record.status === "active" ? "success" : "danger"}>{record.status === "active" ? "有效销课" : "已撤销"}</StatusBadge>
                                </article>
                              );
                            })}
                          </div>
                        ) : (
                          <p className="muted-copy">暂无新系统逐笔销课记录。</p>
                        )}
                      </details>
                    );
                  })}
                </div>
              </Card>
            );
          })}
        </div>
      ) : (
        <EmptyState title="未找到会员或课程" description="请检查姓名、手机号或课程名称是否正确。" />
      )}
    </section>
  );
}

function unit(value: number) {
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
}
function date(value: string) {
  return new Date(value).toLocaleDateString("zh-CN");
}
function dateTime(value: string) {
  return new Date(value).toLocaleString("zh-CN", { hour12: false });
}
function packageStatus(status: string) {
  return ({ active: "在用", closed: "已销完", expired: "已到期", frozen: "已冻结" } as Record<string, string>)[status] ?? status;
}
